/**
 * Canvas UI 2.0 — the "Prompt Library" picker in the Refine tab.
 *
 * A closed dropdown that opens UPWARD into a list of curated prompts for the
 * owning node type. Picking one INSERTS its text at the caret and closes; it
 * does NOT generate. The user is expected to edit before submitting, which is
 * why this fills the field rather than firing a run — and why it inserts
 * rather than replaces, so anything already typed survives.
 *
 * Renders nothing when the node type has no library (see `promptLibraryFor`),
 * so ImageGenerator and StoryboardDesigner keep their current Refine tab
 * untouched rather than showing an empty control.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createNadDebug } from '../../../../utils/debugLog'
import { promptLibraryFor } from './promptLibrary'

const promptLibraryLog = createNadDebug('dw:prompt-library')

export interface PromptLibrarySelectProps {
  /** Frontend node type — selects which curated list to offer. */
  nodeType: string | undefined
  /** Owning node id, for the NAD trace. */
  nodeId: string
  /** Receives the chosen prompt text, to insert at the caret. */
  onSelect: (prompt: string) => void
  /** Disabled while the node is running or the panel is read-only. */
  disabled?: boolean
}

const PromptLibrarySelect = memo(function PromptLibrarySelect({
  nodeType,
  nodeId,
  onSelect,
  disabled = false,
}: PromptLibrarySelectProps) {
  const { t } = useTranslation('canvas')
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const entries = promptLibraryFor(nodeType)

  // Click-outside — same pattern as the Refine retry popover directly below it.
  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof HTMLElement)) return
      const target = e.target
      if (
        listRef.current &&
        !listRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  // Escape closes without selecting — the list covers the feedback box it is
  // about to write into, so a no-op exit has to be reachable from the keyboard.
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const handleSelect = useCallback(
    (id: string, prompt: string) => {
      promptLibraryLog({
        event: 'prompt-library:selected',
        trace_id: `prompt-library:${nodeId}`,
        node_id: nodeId,
        node_type: nodeType ?? 'unknown',
        entry_id: id,
        prompt_chars: prompt.length,
      })
      onSelect(prompt)
      setIsOpen(false)
    },
    [nodeId, nodeType, onSelect],
  )

  if (entries.length === 0) return null

  const label = t('promptLibrary.label')

  return (
    <div className="relative shrink-0" data-testid="prompt-library">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-input-border bg-input-bg px-2.5 py-1.5 text-xxs font-medium text-text-muted transition-colors hover:border-input-border-hover hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 nodrag"
        data-testid="prompt-library-trigger"
      >
        <span>{label}</span>
        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          aria-label={label}
          // Opens upward: the Refine tab sits at the bottom of a panel that is
          // itself anchored under the node card, so a downward list would open
          // off the bottom of the viewport on any node low in the canvas.
          className="absolute bottom-full left-0 right-0 z-50 mb-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-1 py-1 shadow-2xl nowheel nodrag"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          data-testid="prompt-library-list"
        >
          {entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="option"
              aria-selected={false}
              title={entry.prompt}
              onClick={() => handleSelect(entry.id, entry.prompt)}
              className="w-full px-3 py-1.5 text-left text-xxs text-text-muted transition-colors hover:bg-surface-2 hover:text-foreground"
              data-testid={`prompt-library-option-${entry.id}`}
            >
              {t(`promptLibrary.${entry.id}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

PromptLibrarySelect.displayName = 'PromptLibrarySelect'

export { PromptLibrarySelect }
